import { z } from 'zod';

export const isZodObject = (body: unknown): body is z.ZodObject<any, any> => {
  return (body as z.ZodObject<any, any>)?.safeParse !== undefined;
};

/**
 * Builds a PATCH-style schema where an omitted field stays omitted.
 *
 * `.partial()` alone does not do this. It wraps each field in `ZodOptional`,
 * but `ZodOptional<ZodDefault<T>>` still produces the default for a missing key,
 * so the parsed body comes back fully populated and a caller that meant to send
 * two fields ends up writing all of them. Verified against zod 4.4.3.
 *
 * Each field is therefore unwrapped out of its `ZodDefault` before being made
 * optional. Validators (min/max/enum) are on the inner type and survive.
 * Only the outermost default is removed - defaults nested inside an object field
 * still apply once that field is sent.
 */
type TUnwrapDefault<TField> =
  TField extends z.ZodDefault<infer TInner> ? TInner : TField;

type TPartialWithoutDefaults<TShape extends z.ZodRawShape> = z.ZodObject<{
  [TKey in keyof TShape]: z.ZodOptional<TUnwrapDefault<TShape[TKey]>>;
}>;

export const partialWithoutDefaults = <TShape extends z.ZodRawShape>(
  schema: z.ZodObject<TShape>,
): TPartialWithoutDefaults<TShape> => {
  const shape = Object.fromEntries(
    Object.entries(schema.shape).map(([field, validator]) => {
      const isDefaulted = (validator as z.ZodType)?.def?.type === 'default';
      const inner = isDefaulted
        ? (validator as z.ZodDefault<z.ZodType>).unwrap()
        : (validator as z.ZodType);

      return [field, inner.optional()];
    }),
  );

  // * The mapped return type mirrors what the loop above builds; the shape is
  // * assembled dynamically, so the cast is the only way to state it.
  return z.object(shape) as unknown as TPartialWithoutDefaults<TShape>;
};

const getResultForZodParse = (
  schema: z.ZodObject<any, any>,
  data: unknown,
  { allowEmptyBody = false } = {},
) => {
  const { success: schemaIsUndefined } = schema.safeParse(undefined);

  if (allowEmptyBody && schemaIsUndefined) {
    // * Since body-parser changes body of request to {} we need to check that
    // * in case the schema defined is undefined
    const result = schema.or(z.object({})).safeParse(data);
    return result;
  }

  const result = schema.safeParse(data);
  return result;
};

export const checkZodSchema = (
  data: unknown,
  schema: unknown,
  { passThroughExtraKeys = false, allowEmptyBody = false } = {},
):
  | {
      success: true;
      data: unknown;
    }
  | {
      success: false;
      error: unknown;
    } => {
  if (isZodObject(schema)) {
    const result = getResultForZodParse(schema, data, { allowEmptyBody });

    if (!result.success) {
      return {
        success: false,
        error: result?.error,
      };
    }

    return {
      success: true,
      data:
        passThroughExtraKeys && typeof data === 'object'
          ? { ...data, ...result.data }
          : result.data,
    };
  }

  return {
    success: true,
    data: data,
  };
};
